import React from 'react';
import i18next, { StringMap, TFunctionKeys, TFunctionResult, TOptions } from 'i18next';
import { BackendModule, SexModule, BackendOptions, Format, initFormat } from '@lang-tools';
import { LangConfig, LangProjectName } from './helpers';

export * from './helpers';

/** i18next TFunction, compatible with react18 */
interface TFunction {
  // basic usage
  <
    TResult extends TFunctionResult | React.ReactNode = string,
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string,
  ): TResult;
  // overloaded usage
  <
    TResult extends TFunctionResult | React.ReactNode = string,
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string,
  ): TResult;
}

interface InitLangOptions {
  config: LangConfig;
  vkMainUrl: string;
  forceFallback?: boolean;
  langFallback?: (lang: string) => Promise<any>;
  debug?: boolean;
  project: LangProjectName;
  sourceUrl?: string;
}

export interface Lang {
  t: TFunction;
  i18n: ReturnType<typeof i18next.createInstance>;
  language: string;
  format: Format;
  init(options: InitLangOptions): Promise<this>;
  project: LangProjectName;
  projectName: {
    nominative: string;
    genitive: string;
    dative: string;
    locative: string;
  };
}

export const L: Lang = {
  t: (value: string | string[]) => (Array.isArray(value) ? value[value.length - 1] : value),
  i18n: i18next.createInstance(),
  language: 'ru',
  format: initFormat(() => ''),
  project: 'vk',
  projectName: {
    nominative: '',
    genitive: '',
    dative: '',
    locative: '',
  },
  init({ forceFallback, debug, langFallback, project, config, sourceUrl }: InitLangOptions) {
    return new Promise((resolve, reject) => {
      const { name, lang, prefix } = config;

      const backend: BackendOptions = {
        defaultNsPrefix: prefix,
        name,
        sourceUrl,
        source: forceFallback ? 'fallback' : 'url',
        fallback: langFallback ? () => langFallback(lang) : void 0,
      };

      this.language = lang;

      const instance = i18next.createInstance().use(BackendModule).use(SexModule);

      this.i18n = instance;

      instance.init(
        {
          lng: lang,
          postProcess: [SexModule.name],
          backend,
          debug,
          interpolation: { escapeValue: false },
        },
        (err: any, t) => {
          if (err) {
            reject(err);
          }
          this.t = t;
          this.format = initFormat(this.t);
          this.project = project;
          this.projectName = {
            nominative: t(`project_name_${project}`),
            genitive: t(`project_name_${project}_genitive`),
            dative: t(`project_name_${project}_dative`),
            locative: t(`project_name_${project}_locative`),
          };
          resolve(this);
        },
      );
    });
  },
};
